import { useMemo, useState } from 'react';
import { Sparkles, MapPin, Building2, Plus, Check, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import type { ConventionEmployee, Proposition } from '../types/convention';
import type { JobOffer } from '../types/jobOffer';
import { rankOffers, hasMatchingProfile, type PreparedOffer } from '../utils/jobMatching';

interface RecommendedOffersProps {
  employee: ConventionEmployee;
  propositions: Proposition[];
  preparedOffers: PreparedOffer[];
  darkMode: boolean;
  onAdd: (offer: JobOffer) => Promise<void>;
}

const INITIAL_COUNT = 5;
const MAX_COUNT = 15;

function scoreStyle(score: number, darkMode: boolean): string {
  if (score >= 70) return darkMode ? 'bg-emerald-900/40 text-emerald-300 border-emerald-700' : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (score >= 45) return darkMode ? 'bg-sky-900/40 text-sky-300 border-sky-700' : 'bg-sky-50 text-sky-700 border-sky-200';
  return darkMode ? 'bg-amber-900/30 text-amber-300 border-amber-700' : 'bg-amber-50 text-amber-700 border-amber-200';
}

function barColor(score: number): string {
  if (score >= 70) return 'bg-emerald-500';
  if (score >= 45) return 'bg-sky-500';
  return 'bg-amber-500';
}

export default function RecommendedOffers({ employee, propositions, preparedOffers, darkMode, onAdd }: RecommendedOffersProps) {
  const [expanded, setExpanded] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);

  const matches = useMemo(() => {
    const excluded = new Set(propositions.map(p => p.offerId).filter(Boolean));
    return rankOffers(employee, preparedOffers, excluded);
  }, [employee, preparedOffers, propositions]);

  const visible = matches.slice(0, expanded ? MAX_COUNT : INITIAL_COUNT);
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';

  const handleAdd = async (offer: JobOffer) => {
    setAddingId(offer.id);
    try {
      await onAdd(offer);
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div className={`rounded-xl border p-4 ${darkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-gradient-to-br from-sky-50/60 to-white border-sky-100'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`flex items-center gap-2 text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <Sparkles className="w-4 h-4 text-sky-500" />
          Offres recommandées
        </div>
        {matches.length > 0 && (
          <span className={`text-xs ${muted}`}>{matches.length} compatible{matches.length > 1 ? 's' : ''}</span>
        )}
      </div>

      {!hasMatchingProfile(employee) ? (
        <p className={`text-sm ${muted}`}>
          Renseignez le poste actuel ou le projet validé du salarié pour obtenir des recommandations.
        </p>
      ) : matches.length === 0 ? (
        <p className={`text-sm ${muted}`}>
          Aucune offre compatible pour le moment. Les nouvelles offres seront analysées automatiquement.
        </p>
      ) : (
        <div className="space-y-2">
          {visible.map(({ offer, score, reasons, warnings }) => (
            <div
              key={offer.id}
              className={`group rounded-lg border p-3 transition-all duration-200 hover:shadow-md ${darkMode ? 'bg-gray-800 border-gray-700 hover:border-sky-600' : 'bg-white border-gray-200 hover:border-sky-300'}`}
            >
              <div className="flex items-start gap-3">
                <div className={`shrink-0 w-14 text-center rounded-lg border px-1 py-1.5 ${scoreStyle(score, darkMode)}`}>
                  <div className="text-base font-bold leading-tight">{score}%</div>
                  <div className={`mt-1 h-1 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div className={`h-full ${barColor(score)} transition-all duration-500`} style={{ width: `${score}%` }} />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{offer.title}</div>
                  <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs mt-0.5 ${muted}`}>
                    <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{offer.company || '—'}</span>
                    {offer.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{offer.location}</span>}
                    {offer.type && <span>{offer.type}</span>}
                    {offer.aiSource && <span className={darkMode ? 'text-sky-400' : 'text-sky-600'}>{offer.aiSource}</span>}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {reasons.map(r => (
                      <span key={r} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${darkMode ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-50 text-emerald-700'}`}>
                        <Check className="w-3 h-3" />{r}
                      </span>
                    ))}
                    {warnings.map(w => (
                      <span key={w} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        <AlertTriangle className="w-3 h-3" />{w}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => handleAdd(offer)}
                  disabled={addingId !== null}
                  title="Ajouter aux propositions"
                  className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${darkMode ? 'bg-sky-600 text-white hover:bg-sky-500' : 'bg-sky-600 text-white hover:bg-sky-700'}`}
                >
                  <Plus className="w-3.5 h-3.5" />
                  {addingId === offer.id ? 'Ajout...' : 'Proposer'}
                </button>
              </div>
            </div>
          ))}
          {matches.length > INITIAL_COUNT && (
            <button
              onClick={() => setExpanded(v => !v)}
              className={`w-full flex items-center justify-center gap-1 py-1.5 text-xs font-medium rounded-lg transition-colors ${darkMode ? 'text-sky-300 hover:bg-gray-700' : 'text-sky-700 hover:bg-sky-50'}`}
            >
              {expanded ? <><ChevronUp className="w-3.5 h-3.5" />Voir moins</> : <><ChevronDown className="w-3.5 h-3.5" />Voir plus ({Math.min(matches.length, MAX_COUNT) - INITIAL_COUNT})</>}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
